import { useEffect, useRef, useState } from 'react';
import * as providerAdminApi from '../../../api/providerAdminApi';
import { Modal } from '../../../components/ui/Modal';

const cancelButtonClass =
  'rounded-xl border border-eorig-border bg-white px-4 py-2 text-sm font-semibold text-eorig-ink hover:bg-eorig-surface disabled:opacity-50';
const dangerButtonClass =
  'rounded-xl bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-50';

/**
 * Confirm-and-delete for a Provider Admin organization.
 * Parent owns `open`; this owns the API call and surfaces the backend
 * message (e.g. the 409 "still has N programs" guard) inline.
 */
export function DeleteOrganizationDialog({ org, open, onClose, onDeleted }) {
  const cancelRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setBusy(false);
      setError(null);
    }
  }, [open]);

  if (!org) return null;

  function close() {
    if (busy) return;
    onClose?.();
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await providerAdminApi.deleteOrganization(org.id);
      onDeleted?.(org);
    } catch (err) {
      setError(err.message || 'Delete failed');
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={`Delete ${org.name}?`}
      description={`${org.code} · ${org.org_type}`}
      initialFocusRef={cancelRef}
      footer={
        <>
          <button
            ref={cancelRef}
            type="button"
            className={cancelButtonClass}
            disabled={busy}
            onClick={close}
          >
            Cancel
          </button>
          <button
            type="button"
            className={dangerButtonClass}
            disabled={busy}
            onClick={confirm}
          >
            {busy ? 'Deleting…' : 'Delete organization'}
          </button>
        </>
      }
    >
      <p className="text-sm leading-5 text-eorig-muted">
        This permanently removes the organization along with its pending invitations,
        connections and Digitize binding. It cannot be undone.
      </p>
      <p className="mt-2 text-sm leading-5 text-eorig-muted">
        Organizations that still hold packages, programs, user accounts or exports cannot be
        deleted — suspend them instead.
      </p>
      {error ? (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
    </Modal>
  );
}
